import {
  APPROVAL_MINE_ROLES,
  APPROVAL_QUEUE_ROLES,
  COMMISSION_LEADERBOARD_ROLES,
  COMMISSION_REPORT_VIEW_ROLES,
  COMMISSION_SETTLEMENT_ROLES,
  CUSTOMER_READ_ROLES,
  INVITE_CODE_VIEW_ROLES,
  MODEL_PLAN_VIEW_ROLES,
  RECEIPT_VIEW_ROLES,
  ROLE_OPS_ADMIN,
  ROLE_OPS_CUSTOMER_MANAGER,
  ROLE_OPS_FINANCE_MANAGER,
  ROLE_OPS_SUPPORT,
  SALES_COST_MANAGE_ROLES,
  SERVICE_LEVEL_MANAGE_ROLES,
  hasAnyOpsRole,
  hasExplicitOpsRole,
  type OpsRole,
} from '../auth/opsRoles.ts';
import type { I18nKey } from '../i18n/index.ts';

export interface NavItem {
  readonly to: string;
  readonly labelKey: I18nKey;
  readonly testId: string;
  readonly icon: string;
  readonly requiredRoles: readonly OpsRole[];
  readonly supportOnly?: boolean;
  /**
   * `testId` of another nav item that renders the SAME `to` for a narrower
   * audience (#2265). While that item is visible to the session, this one
   * is hidden, so a page reachable from two groups still appears exactly
   * once — in the sidebar, in the ⌘K palette, as one active-highlight row,
   * and as one page-title candidate.
   *
   * Suppression is derived from the superseding item's OWN `requiredRoles`
   * rather than from a second role list copied here, so the two guards
   * cannot drift apart and leave a session seeing both entries or neither.
   */
  readonly supersededBy?: string;
}

export interface NavGroup {
  readonly titleKey: I18nKey;
  readonly items: readonly NavItem[];
}

/**
 * Canonical operator navigation. `ops-admin` inherits `ops-support`, but
 * support-only helper pages stay out of the admin sidebar to avoid
 * duplicate entries beside the broader admin tools.
 */
export const NAV_GROUPS: readonly NavGroup[] = [
  {
    // Business cockpit IA (epic #997): the operator-loop pages only —
    // cockpit, true P&L, SKU economics, risk workbench. The projection
    // pages (customers/overview/supply/risk) were retired in #1007 and the
    // traffic monitor moved to the System group (it is on-call triage, not
    // business reporting).
    titleKey: 'shell.group.business',
    items: [
      {
        to: '/business/overview',
        labelKey: 'shell.nav.business_overview',
        testId: 'business-overview',
        icon: 'space_dashboard',
        requiredRoles: [ROLE_OPS_ADMIN],
      },
      {
        // Consolidated metrics hub (epic #1643). Sits right after the
        // cockpit; the standalone usage/pnl/sku entries were absorbed
        // into its tabs in S5 (their routes redirect here).
        to: '/business/metrics',
        labelKey: 'shell.nav.business_metrics',
        testId: 'business-metrics',
        icon: 'assessment',
        requiredRoles: [ROLE_OPS_ADMIN],
      },
      {
        to: '/business/risk',
        labelKey: 'shell.nav.business_risk',
        testId: 'business-risk',
        icon: 'crisis_alert',
        requiredRoles: [ROLE_OPS_ADMIN],
      },
    ],
  },
  {
    titleKey: 'shell.group.customers',
    items: [
      {
        to: '/crm',
        labelKey: 'shell.nav.customer_manager_workspace',
        testId: 'customer-manager-workspace',
        icon: 'support_agent',
        requiredRoles: [ROLE_OPS_ADMIN, ROLE_OPS_CUSTOMER_MANAGER],
      },
      {
        to: '/crm/requests',
        labelKey: 'shell.nav.customer_manager_request_logs',
        testId: 'customer-manager-request-logs',
        icon: 'manage_search',
        requiredRoles: [ROLE_OPS_ADMIN, ROLE_OPS_CUSTOMER_MANAGER],
      },
      {
        to: '/crm/customer-pool',
        labelKey: 'shell.nav.customer_pool',
        testId: 'customer-pool',
        icon: 'inventory_2',
        requiredRoles: [ROLE_OPS_ADMIN, ROLE_OPS_CUSTOMER_MANAGER],
      },
      {
        // Scoped usage overview (epic #1035 E6-D4, #1068): the sales
        // projection of the usage aggregate — book-scoped server-side.
        to: '/crm/usage',
        labelKey: 'shell.nav.crm_usage',
        testId: 'crm-usage',
        icon: 'query_stats',
        requiredRoles: [ROLE_OPS_ADMIN, ROLE_OPS_CUSTOMER_MANAGER],
      },
      {
        // Lead pricing panel (epic #1032 E3-P1, #1102): per-SKU × brand
        // sales costs. Admin + sales lead only — same-source role set as
        // the matrix entry and the App.tsx guard.
        to: '/crm/pricing',
        labelKey: 'shell.nav.lead_pricing',
        testId: 'lead-pricing',
        icon: 'sell',
        requiredRoles: SALES_COST_MANAGE_ROLES,
      },
      {
        // Commission + book-performance report (epic #1034 E5-M5, #1147):
        // CM own commission statements + own book; lead/admin/finance see
        // wider scope. Same-source role set as the matrix + App.tsx guard.
        to: '/crm/commission',
        labelKey: 'shell.nav.crm_commission',
        testId: 'crm-commission',
        icon: 'paid',
        requiredRoles: COMMISSION_REPORT_VIEW_ROLES,
      },
      {
        // Commission leaderboard (epic #1034 E5-M5, #1147): its own
        // finance-reachable page — admin/finance/lead, NOT CM. Kept off the
        // procurement-bearing lead pricing page so finance never sees
        // procurement.
        to: '/crm/leaderboard',
        labelKey: 'shell.nav.crm_leaderboard',
        testId: 'crm-leaderboard',
        icon: 'leaderboard',
        requiredRoles: COMMISSION_LEADERBOARD_ROLES,
      },
      {
        // Salesperson invite codes (epic #1475): one role-adaptive page —
        // CM reads its own code + today's headroom, lead/admin mint and
        // retire, admin additionally edits the activation settings. The
        // nav guard is the VIEW set, same source as the matrix entry and
        // the App.tsx route guard.
        to: '/crm/invite-codes',
        labelKey: 'shell.nav.invite_codes',
        testId: 'crm-invite-codes',
        icon: 'confirmation_number',
        requiredRoles: INVITE_CODE_VIEW_ROLES,
      },
      {
        // Approval center (epic #1030 E1-S3, #1039). Two role-gated
        // entries onto the same page: approvers land on the queue tab,
        // sales roles land on my-submissions. Both surface in the ⌘K
        // palette through the shared nav registry.
        //
        // The guard stays `APPROVAL_QUEUE_ROLES` so a sales lead and an
        // admin-only session see exactly what they saw before #2265. That
        // set also contains the finance role, so a finance session would
        // match here as well as in the finance group — `supersededBy`
        // resolves that in favour of finance's own workspace.
        to: '/approvals/queue',
        labelKey: 'shell.nav.approval_queue',
        testId: 'approval-queue',
        icon: 'rule',
        requiredRoles: APPROVAL_QUEUE_ROLES,
        supersededBy: 'finance-approval-queue',
      },
      {
        to: '/approvals/mine',
        labelKey: 'shell.nav.approvals_mine',
        testId: 'approvals-mine',
        icon: 'assignment',
        requiredRoles: APPROVAL_MINE_ROLES,
      },
      {
        // Distinct key from the admin `/customers/requests` "请求日志" item
        // below (#1007): the support surface is a tenant request lookup, so
        // its label matches that page's own title instead of sharing one key.
        to: '/support/requests',
        labelKey: 'shell.nav.support_requests',
        testId: 'support-requests',
        icon: 'article',
        requiredRoles: [ROLE_OPS_SUPPORT],
        supportOnly: true,
      },
      {
        // Support-ticket queue (#1394) — the ticket system, distinct from
        // the request-log lookup above; the two share only the role gate.
        //
        // NOT supportOnly, unlike its neighbour (#1893). That flag hides an
        // item from an explicit ops-admin, and above it earns its keep: the
        // support request lookup has an admin-facing twin
        // (`/customers/requests`), so hiding one of them is de-duplication.
        // The ticket queue has no twin. Carrying the flag here would apply a
        // de-duplication rule where there is nothing to de-duplicate, and the
        // result is not a tidier sidebar — it is the only entrance to the
        // feature missing for the operators most likely to staff it.
        // Access was never in question: `expandOpsRoles` gives admin ⊃
        // support, so the route guard and the ops-api gate both already
        // admitted them; only the link was gone.
        to: '/support/tickets',
        labelKey: 'shell.nav.support_tickets',
        testId: 'support-tickets',
        icon: 'confirmation_number',
        requiredRoles: [ROLE_OPS_SUPPORT],
      },
      {
        // Fleet-wide tenant announcements (#1396 follow-up). NOT supportOnly
        // and NOT a support role: one send reaches every customer and cannot
        // be recalled, so it sits with the admin surfaces even though it
        // lives in the same product area as the ticket queue above.
        to: '/support/announcements',
        labelKey: 'shell.nav.announcements',
        testId: 'announcements',
        icon: 'notifications',
        requiredRoles: [ROLE_OPS_ADMIN],
      },
      {
        to: '/customers/accounts',
        labelKey: 'shell.nav.accounts',
        testId: 'accounts',
        icon: 'account_circle',
        requiredRoles: [ROLE_OPS_ADMIN],
      },
      {
        // #1040: customer managers reach the ownership page as a read-only
        // brand-scoped "my book" view — the App.tsx route guard has allowed
        // this for a while; the nav entry now matches it (and the
        // OPS_UI_ROUTE_PERMISSIONS matrix) instead of hiding the page.
        to: '/customers/ownership',
        labelKey: 'shell.nav.customer_ownership',
        testId: 'customer-ownership',
        icon: 'assignment_ind',
        requiredRoles: [ROLE_OPS_ADMIN, ROLE_OPS_CUSTOMER_MANAGER],
      },
      {
        to: '/customers/requests',
        labelKey: 'shell.nav.request_logs',
        testId: 'request-logs',
        icon: 'article',
        requiredRoles: [ROLE_OPS_ADMIN],
      },
      {
        to: '/customers/billing-anomalies',
        labelKey: 'shell.nav.billing_anomalies',
        testId: 'billing-anomalies',
        icon: 'rule',
        requiredRoles: [ROLE_OPS_ADMIN],
      },
      {
        // Customer balance alerts (#1892). Admin-only, same-source with the
        // OPS_UI_ROUTE_PERMISSIONS entry and the App.tsx guard. Three of the
        // four surfaces on this page are levers on a channel that mails paying
        // customers — the switch decides whether ANY alert goes out — and the
        // fourth lists customer addresses next to their balances.
        to: '/customers/balance-alerts',
        labelKey: 'shell.nav.balance_alerts',
        testId: 'balance-alerts',
        icon: 'crisis_alert',
        requiredRoles: [ROLE_OPS_ADMIN],
      },
      {
        // Codex at-capacity alerts (#2575). Admin-only, same-source with the
        // OPS_UI_ROUTE_PERMISSIONS entry and the App.tsx guard. The switch
        // decides whether ANY Codex capacity mail goes out; test-send is a
        // real unrecallable delivery; a mute silences a named customer;
        // preview lists customer emails.
        to: '/customers/codex-capacity-alerts',
        labelKey: 'shell.nav.codex_capacity_alerts',
        testId: 'codex-capacity-alerts',
        icon: 'query_stats',
        requiredRoles: [ROLE_OPS_ADMIN],
      },
      {
        // Operator-authored customer mail (#1911). Next to the balance alert
        // rather than under Supply, where it used to sit as "model notices":
        // both pages put a message in a paying customer's inbox, and selecting
        // by model turned out to be one audience policy rather than what the
        // tool is. Same-source role set with the OPS_UI_ROUTE_PERMISSIONS entry
        // and the App.tsx guard — one press is an unrecallable bulk mail.
        to: '/customers/email-notices',
        labelKey: 'shell.nav.email_notices',
        testId: 'email-notices',
        icon: 'mail',
        requiredRoles: [ROLE_OPS_ADMIN],
      },
    ],
  },
  {
    titleKey: 'shell.group.finance',
    items: [
      {
        // Finance's own door to the approval center (#2265). Finance is
        // the stage-2 signer of credit applications, so the queue is part
        // of the finance workspace rather than of the sales one it used to
        // be filed under. Same route and same page as the customers-group
        // entry above — only the entry point differs, and that entry hides
        // itself here via `supersededBy` so nothing renders twice.
        //
        // Deliberately NOT `APPROVAL_QUEUE_ROLES`: that set includes admin
        // and the sales lead, whose home for this page stays the customers
        // group. `hasAnyOpsRole` expansion means `ops-super-admin` reaches
        // it too, which is correct — it holds the finance role.
        to: '/approvals/queue',
        labelKey: 'shell.nav.approval_queue',
        testId: 'finance-approval-queue',
        icon: 'rule',
        requiredRoles: [ROLE_OPS_FINANCE_MANAGER],
      },
      {
        // Receipt console (epic #1031 E2-R5, #1063): finance-manager and
        // admin get the verification queue; CM/lead land on their
        // server-scoped submission list. Role set is the SAME contracts
        // constant as the matrix entry and the App.tsx guard.
        to: '/finance/receipts',
        labelKey: 'shell.nav.receipts',
        testId: 'receipts',
        icon: 'receipt_long',
        requiredRoles: RECEIPT_VIEW_ROLES,
      },
      {
        // Commission settlement queue (epic #1034 E5-M4, #1146):
        // finance-manager + admin only. Same-source role set as the
        // matrix entry and the App.tsx guard.
        to: '/finance/commission-settlement',
        labelKey: 'shell.nav.commission_settlement',
        testId: 'commission-settlement',
        icon: 'paid',
        requiredRoles: COMMISSION_SETTLEMENT_ROLES,
      },
      {
        to: '/finance/payment-orders',
        labelKey: 'shell.nav.payments_orders',
        testId: 'payment-orders',
        icon: 'receipt_long',
        requiredRoles: [ROLE_OPS_ADMIN],
      },
      {
        to: '/finance/payment-providers',
        labelKey: 'shell.nav.payment_providers',
        testId: 'payment-providers',
        icon: 'credit_card',
        requiredRoles: [ROLE_OPS_ADMIN],
      },
      {
        to: '/finance/gift-cards',
        labelKey: 'shell.nav.gift_cards',
        testId: 'gift-cards',
        icon: 'redeem',
        requiredRoles: [ROLE_OPS_ADMIN],
      },
      {
        // Billing-statement read surface (epic #1033 E4, #1956): read-only AR
        // list. Same-source role set as the matrix entry and the App.tsx
        // guard — admin + customer-manager + finance-manager.
        to: '/finance/billing-statements',
        labelKey: 'shell.nav.billing_statements',
        testId: 'billing-statements',
        icon: 'article',
        requiredRoles: CUSTOMER_READ_ROLES,
      },
      {
        to: '/finance/credit-settings',
        labelKey: 'shell.nav.credit_settings',
        testId: 'credit-settings',
        icon: 'credit_score',
        requiredRoles: [ROLE_OPS_ADMIN],
      },
      {
        // Credit monitor (router#2014): expands the #2007 overdue page.
        // Role gate is CUSTOMER_READ_ROLES; row/total visibility is the
        // caller's book (same as billing statements). A platform-wide
        // figure is an open product question.
        to: '/finance/credit-overdue',
        labelKey: 'shell.nav.credit_overdue',
        testId: 'credit-overdue',
        icon: 'crisis_alert',
        requiredRoles: CUSTOMER_READ_ROLES,
      },
      {
        // Credit grant history (router#2543): every grant, active and
        // ended, for the caller's visible book. Same-source role set as
        // the matrix entry and the App.tsx guard.
        to: '/finance/credit-grants',
        labelKey: 'shell.nav.credit_grants',
        testId: 'credit-grants',
        icon: 'history',
        requiredRoles: CUSTOMER_READ_ROLES,
      },
      {
        to: '/finance/fx-policy',
        labelKey: 'shell.nav.fx_policy',
        testId: 'fx-policy',
        icon: 'currency_exchange',
        requiredRoles: [ROLE_OPS_ADMIN],
      },
      {
        to: '/finance/reports',
        labelKey: 'shell.nav.reports',
        testId: 'reports',
        icon: 'assessment',
        requiredRoles: [ROLE_OPS_ADMIN],
      },
    ],
  },
  {
    titleKey: 'shell.group.supply',
    items: [
      {
        to: '/supply/upstreams',
        labelKey: 'shell.nav.upstreams',
        testId: 'upstreams',
        icon: 'dns',
        requiredRoles: [ROLE_OPS_ADMIN],
      },
      {
        to: '/supply/tenant-routing',
        labelKey: 'shell.nav.tenant_routing',
        testId: 'tenant-routing',
        icon: 'alt_route',
        requiredRoles: [ROLE_OPS_ADMIN],
      },
      {
        to: '/supply/guaranteed-service',
        labelKey: 'shell.nav.guaranteed_service',
        testId: 'guaranteed-service',
        icon: 'shield_person',
        requiredRoles: [ROLE_OPS_ADMIN],
      },
      {
        to: '/quotas',
        labelKey: 'shell.nav.quotas',
        testId: 'quotas',
        icon: 'tune',
        requiredRoles: [ROLE_OPS_ADMIN],
      },
      {
        // Service level catalogue (#1723/#1725). Same-source role set as the
        // OPS_UI_ROUTE_PERMISSIONS entry and the App.tsx guard — a level names
        // the routing config it anchors, so the page is admin-only.
        to: '/supply/service-levels',
        labelKey: 'shell.nav.service_levels',
        testId: 'service-levels',
        icon: 'view_carousel',
        requiredRoles: SERVICE_LEVEL_MANAGE_ROLES,
      },
      {
        to: '/supply/egress-profiles',
        labelKey: 'shell.nav.egress_profiles',
        testId: 'egress-profiles',
        icon: 'conversion_path',
        requiredRoles: [ROLE_OPS_ADMIN],
      },
      {
        to: '/supply/egress-lines',
        labelKey: 'shell.nav.egress_lines',
        testId: 'egress-lines',
        icon: 'alt_route',
        requiredRoles: [ROLE_OPS_ADMIN],
      },
      {
        // Admission firewall rulesets (#1758/#1760). Admin-only, same-source
        // with the OPS_UI_ROUTE_PERMISSIONS entry and the App.tsx guard. It
        // sits next to egress profiles because a profile references a ruleset,
        // but it is a separate page on purpose: editing one profile's
        // reference and editing a ruleset every profile references have
        // different blast radii.
        to: '/supply/firewall-rulesets',
        labelKey: 'shell.nav.firewall_rulesets',
        testId: 'firewall-rulesets',
        icon: 'shield',
        requiredRoles: [ROLE_OPS_ADMIN],
      },
      {
        to: '/supply/models',
        labelKey: 'shell.nav.models',
        testId: 'models',
        icon: 'view_carousel',
        requiredRoles: [ROLE_OPS_ADMIN],
      },
      {
        to: '/supply/vendors',
        labelKey: 'shell.nav.vendors',
        testId: 'vendors',
        icon: 'storefront',
        requiredRoles: [ROLE_OPS_ADMIN],
      },
      {
        to: '/supply/namespaces',
        labelKey: 'shell.nav.namespaces',
        testId: 'namespaces',
        icon: 'hub',
        requiredRoles: [ROLE_OPS_ADMIN],
      },
      {
        // Grouped under Supply per the #853 IA remediation, but the route
        // deliberately keeps its historical `/governance/error-policy` path
        // this round so bookmarks and deep links stay valid (#855). The
        // path/group mismatch is intentional.
        to: '/governance/error-policy',
        labelKey: 'shell.nav.error_policy',
        testId: 'error-policy',
        icon: 'rule_settings',
        requiredRoles: [ROLE_OPS_ADMIN],
      },
    ],
  },
  {
    // Supply / marketplace domain split (epic #1327 PR-9). The group above
    // is where upstream capacity is bought and made routable; this one is
    // where it is sold as a marketplace SKU. Keeping them in one group is
    // what let SKU creation drift onto the namespace catalog page, so the
    // sidebar now states the boundary the pages enforce.
    titleKey: 'shell.group.marketplace',
    items: [
      {
        to: '/market',
        labelKey: 'shell.nav.model_market',
        testId: 'model-market',
        icon: 'deployed_code',
        requiredRoles: [ROLE_OPS_ADMIN],
      },
      {
        // Model plans (epic #2292 / #2297): catalog templates bound to
        // invite codes. Same-source VIEW set as the matrix entry and the
        // App.tsx guard — manage actions gate inside the page on admin.
        to: '/market/model-plans',
        labelKey: 'shell.nav.model_plans',
        testId: 'model-plans',
        icon: 'view_list',
        requiredRoles: MODEL_PLAN_VIEW_ROLES,
      },
      {
        to: '/market/readiness',
        labelKey: 'shell.nav.sku_activation_readiness',
        testId: 'sku-activation-readiness',
        icon: 'fact_check',
        requiredRoles: [ROLE_OPS_ADMIN],
      },
      {
        to: '/market/default-key-namespaces',
        labelKey: 'shell.nav.default_key_namespaces',
        testId: 'default-key-namespaces',
        icon: 'rule',
        requiredRoles: [ROLE_OPS_ADMIN],
      },
    ],
  },
  {
    titleKey: 'shell.group.governance',
    items: [
      {
        to: '/governance/audit',
        labelKey: 'shell.nav.audit',
        testId: 'audit',
        icon: 'history',
        requiredRoles: [ROLE_OPS_ADMIN],
      },
      {
        to: '/governance/permissions',
        labelKey: 'shell.nav.permissions',
        testId: 'permissions',
        icon: 'shield_person',
        requiredRoles: [ROLE_OPS_ADMIN],
      },
      {
        to: '/governance/router-client-identities',
        labelKey: 'shell.nav.router_client_identities',
        testId: 'router-client-identities',
        icon: 'fingerprint',
        requiredRoles: [ROLE_OPS_ADMIN],
      },
      {
        to: '/governance/ops-users',
        labelKey: 'shell.nav.ops_users',
        testId: 'ops-users',
        icon: 'manage_accounts',
        requiredRoles: [ROLE_OPS_ADMIN],
      },
    ],
  },
  {
    titleKey: 'shell.group.system',
    items: [
      {
        // Traffic monitor is on-call triage, not business reporting (#1007);
        // it moved out of the Business group but keeps its historical
        // `/business/traffic` route so bookmarks and deep links stay valid.
        to: '/business/traffic',
        labelKey: 'shell.nav.traffic_monitor',
        testId: 'business-traffic',
        icon: 'query_stats',
        requiredRoles: [ROLE_OPS_ADMIN],
      },
      {
        to: '/system/webhook-events',
        labelKey: 'shell.nav.webhook_events',
        testId: 'webhook-events',
        icon: 'webhook',
        requiredRoles: [ROLE_OPS_ADMIN],
      },
      {
        to: '/system/vps',
        labelKey: 'shell.nav.vps',
        testId: 'vps',
        icon: 'storage',
        requiredRoles: [ROLE_OPS_ADMIN],
      },
      {
        to: '/system/cron-runs',
        labelKey: 'shell.nav.cron_runs',
        testId: 'cron-runs',
        icon: 'schedule',
        requiredRoles: [ROLE_OPS_ADMIN],
      },
      {
        to: '/system/dr-check',
        labelKey: 'shell.nav.dr_check',
        testId: 'dr-check',
        icon: 'health_and_safety',
        requiredRoles: [ROLE_OPS_ADMIN],
      },
      {
        to: '/system/health-perspective',
        labelKey: 'shell.nav.health_perspective',
        testId: 'health-perspective',
        icon: 'hub',
        requiredRoles: [ROLE_OPS_ADMIN],
      },
    ],
  },
];

export const ALL_NAV_ITEMS: readonly NavItem[] = NAV_GROUPS.flatMap((g) => g.items);
export const OPS_LANDING_PATH = '/landing';
/**
 * Idle authenticated home (router#2260). Login used to send ops-admin to
 * the first nav item (`/business/overview`), which fires the PnL summary
 * on every sign-in. This path is not a nav item: it loads no ops data.
 * Role-less operators still go to `OPS_LANDING_PATH`.
 */
export const OPS_HOME_PATH = '/home';

export function visibleNavGroups(rawRoles: readonly string[]): NavGroup[] {
  return NAV_GROUPS.map((group) => ({
    ...group,
    items: group.items.filter((item) => canSeeNavItem(rawRoles, item)),
  })).filter((group) => group.items.length > 0);
}

export function firstAccessiblePath(rawRoles: readonly string[]): string | null {
  return visibleNavGroups(rawRoles).length > 0 ? OPS_HOME_PATH : OPS_LANDING_PATH;
}

export function canAccessRoles(
  rawRoles: readonly string[],
  requiredRoles: readonly OpsRole[],
): boolean {
  return hasAnyOpsRole(rawRoles, requiredRoles);
}

/**
 * Whether a narrower entry for the same route has taken this one over for
 * this session (#2265). Reads the superseding item's own `requiredRoles`,
 * so "hidden here" and "shown there" are decided by one guard and can
 * never both be false (the page vanishing) or both be true (the page
 * rendering twice). An unresolvable `supersededBy` suppresses nothing —
 * `nav-supersede.test.ts` is what keeps the reference honest.
 */
function isSupersededForRoles(rawRoles: readonly string[], item: NavItem): boolean {
  if (item.supersededBy === undefined) {
    return false;
  }
  const successor = ALL_NAV_ITEMS.find((candidate) => candidate.testId === item.supersededBy);
  return successor !== undefined && canAccessRoles(rawRoles, successor.requiredRoles);
}

export function titleItemForPath(rawRoles: readonly string[], pathname: string): NavItem | null {
  // Superseded entries are excluded here too, not just from the sidebar:
  // two items sharing a `to` are equal under this sort, so the stable sort
  // would hand the page title to whichever is declared first regardless of
  // which group the session actually navigated from (#2265).
  //
  // `supportOnly` deliberately does NOT apply — that flag only keeps a
  // helper page out of the admin sidebar; an admin who opens it should
  // still get its title.
  return [...ALL_NAV_ITEMS]
    .filter(
      (item) =>
        canAccessRoles(rawRoles, item.requiredRoles) && !isSupersededForRoles(rawRoles, item),
    )
    .sort((a, b) => b.to.length - a.to.length)
    .find((item) => (item.to === '/' ? pathname === '/' : pathname.startsWith(item.to))) ?? null;
}

function canSeeNavItem(rawRoles: readonly string[], item: NavItem): boolean {
  if (item.supportOnly === true && hasExplicitOpsRole(rawRoles, ROLE_OPS_ADMIN)) {
    return false;
  }
  if (isSupersededForRoles(rawRoles, item)) {
    return false;
  }
  return canAccessRoles(rawRoles, item.requiredRoles);
}
